import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Check, AlertCircle, Eye, Users, FileText, Plus, Edit2, Save, X, Trash2, Clock } from "lucide-react";
import { useAuth } from "contexts/AuthContext";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import { useNavigate } from "react-router-dom";
import { updateLegalResearchTopic, updateInternalLegalResearchRequest, createLegalResearchTopics } from "services/legalResearchService";
import { useToast } from "contexts/ToastContext";
import FileView from "components/fileView";

// Define the Topic type (same as in your parent)
export interface Topic {
    id: string;
    topic_title: string;
    priority: number | null;
    is_accepted: boolean;
    is_approved: boolean;
    created_at: string;
    updated_at: string;
    internal_research_request: string | null;
    research_request: string;
    team_name?: string;
}

interface TopicListProps {
    formState: Topic[];
    updateTopics: (updater: (prev: Topic[]) => Topic[]) => void;
    status: string;
    edit: boolean;
    requestId: string;
    updateRequest: (token: string, id: string, data: FormData) => Promise<any>;
    finding_assesement?: string;
    forward_status?: string;
    researchTeamId?: string;
    schedules?: Array<{
        id: string;
        scheduled_datetime: string;
        purpose: string;
        remark?: string;
        location?: string;
    }>;
}

export default function TopicList({ formState, updateTopics, status, edit, requestId, updateRequest, finding_assesement, forward_status, researchTeamId, schedules }: TopicListProps) {
    const { t } = useTranslation();
    const sensors = useSensors(useSensor(PointerSensor));
    const contextData = useAuth();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
    
    // State for editing and adding topics
    const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
    const [editingTopicTitle, setEditingTopicTitle] = useState("");
    const [isAddingTopic, setIsAddingTopic] = useState(false);
    const [newTopicTitle, setNewTopicTitle] = useState("");
    const [deletingTopicId, setDeletingTopicId] = useState<string | null>(null);
    const [isUnacceptedOpen, setIsUnacceptedOpen] = useState(false);

    // Check if user is a team leader
    const isTeamLeader = useMemo(() => {
        if (!formState || !contextData?.user?.user_id) return false;
        return formState.some((topic: any) => 
            topic.team_name && 
            topic.is_accepted
        );
    }, [formState, contextData?.user?.user_id]);

    // Check if user can edit/add topics - only for LegalResearchProsecutor role when status is title_requested
    const canEditTopics = useMemo(() => {
        const userRoles = contextData?.user?.roles || [];
        const isLegalResearchProsecutor = userRoles.some((p) => p === "LegalResearchProsecutor");
        
        return edit || 
            (isLegalResearchProsecutor && 
             status === LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value);
    }, [edit, status, contextData?.user?.roles]);

    const handleStartEdit = (topic: Topic) => {
        setEditingTopicId(topic.id);
        setEditingTopicTitle(topic.topic_title);
    };

    const handleCancelEdit = () => {
        setEditingTopicId(null);
        setEditingTopicTitle("");
    };

    const handleSaveEdit = async (topicId: string) => {
        if (!editingTopicTitle.trim()) {
            showToast(t("TopicTitleCannotBeEmpty"), "error");
            return;
        }
        
        setSubmitting(prev => ({ ...prev, [topicId]: true }));
        try {
            const formData = new FormData();
            formData.append('topic_title', editingTopicTitle.trim());
            await updateLegalResearchTopic(contextData.token, topicId, formData);
            updateTopics((topics) =>
                topics.map((t) =>
                    t.id === topicId ? { ...t, topic_title: editingTopicTitle.trim() } : t
                )
            );
            showToast(t("TopicUpdatedSuccessfully"), "success");
            setEditingTopicId(null);
            setEditingTopicTitle("");
        } catch (error) {
            console.error("Error updating topic:", error);
            showToast(t("FailedToUpdateTopic"), "error");
        } finally {
            setSubmitting(prev => ({ ...prev, [topicId]: false }));
        }
    };

    const handleStartAddTopic = () => {
        setIsAddingTopic(true);
        setNewTopicTitle("");
    };

    const handleCancelAddTopic = () => {
        setIsAddingTopic(false);
        setNewTopicTitle("");
    };

    const handleSaveNewTopic = async () => {
        if (!newTopicTitle.trim()) {
            showToast(t("TopicTitleCannotBeEmpty"), "error");
            return;
        }

        setSubmitting(prev => ({ ...prev, ['new_topic']: true }));
        try {
            const formData = new FormData();
            formData.append('topic_title', newTopicTitle.trim());
            formData.append('research_request', requestId);
            formData.append('is_accepted', 'false');
            formData.append('is_approved', 'false');
            
            if (researchTeamId) {
                formData.append('research_team', researchTeamId);
            }
            
            const newTopic = await createLegalResearchTopics(contextData.token, formData);
            
            updateTopics((topics) => [...topics, newTopic]);
            
            showToast(t("TopicAddedSuccessfully"), "success");
            setIsAddingTopic(false);
            setNewTopicTitle("");
        } catch (error) {
            console.error("Error adding topic:", error);
            showToast(t("FailedToAddTopic"), "error");
        } finally {
            setSubmitting(prev => ({ ...prev, ['new_topic']: false }));
        }
    };

    const handleDeleteTopic = async (topicId: string) => {
        if (!window.confirm(t("ConfirmRemoveTopic"))) {
            return;
        }
        
        setDeletingTopicId(topicId);
        try {
            const formData = new FormData();
            formData.append('is_accepted', 'false');
            await updateLegalResearchTopic(contextData.token, topicId, formData);
            updateTopics((topics) => topics.filter((t) => t.id !== topicId));
            showToast(t("TopicRemoved"), "success");
        } catch (error) {
            console.error("Error deleting topic:", error);
            showToast(t("FailedToRemoveTopic"), "error");
        } finally {
            setDeletingTopicId(null);
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = formState.findIndex((t) => t.id === active.id);
        const newIndex = formState.findIndex((t) => t.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(formState, oldIndex, newIndex).map((t, i) => ({
            ...t,
            priority: i + 1,
        }));

        updateTopics(() => reordered);

        try {
            setSubmitting(prev => ({ ...prev, ['reorder']: true }));
            
            for (const topic of reordered) {
                const originalTopic = formState.find(t => t.id === topic.id);
                if (originalTopic && originalTopic.priority !== topic.priority) {
                    const formData = new FormData();
                    formData.append('priority', topic.priority.toString());
                    await updateLegalResearchTopic(contextData.token, topic.id, formData);
                }
            }
            
            showToast(t("TopicPrioritiesUpdated"), "success");
        } catch (error) {
            console.error("Error updating topic priorities:", error);
            showToast(t("FailedToUpdateTopicPriorities"), "error");
        } finally {
            setSubmitting(prev => ({ ...prev, ['reorder']: false }));
        }
    };

    const toggleApproved = async (id: string) => {
        const topic = formState.find(t => t.id === id);
        if (!topic) return;
        setSubmitting(prev => ({ ...prev, [id]: true }));
        try {
            const formData = new FormData();
            formData.append('is_approved', (!topic.is_approved).toString());
            await updateLegalResearchTopic(contextData.token, id, formData);
            updateTopics((topics) =>
                topics.map((t) =>
                    t.id === id ? { ...t, is_approved: !t.is_approved } : t
                )
            );
        } catch (error) {
            console.error("Error updating topic:", error);
            showToast(t("FailedToUpdateTopic"), "error");
        } finally {
            setSubmitting(prev => ({ ...prev, [id]: false }));
        }
    };
    
    const toggleAccepted = async (id: string) => {
        const topic = formState.find(t => t.id === id);
        if (!topic) return;
        setSubmitting(prev => ({ ...prev, [id]: true }));
        try {
            if (status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value) {
                const data = new FormData();
                data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.proposal_submitted.value);
                data.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value);
                await updateRequest(contextData.token, requestId, data);
                showToast(t("RequestForwardedToCoordinator"), "success");
            } else if (forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value && 
                       contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) {
                const data = new FormData();
                data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value);
                data.append("forward_status", LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value);
                await updateRequest(contextData.token, requestId, data);
                showToast(t("TopicsAcceptedAndForwarded"), "success");
                const formData = new FormData();
                formData.append('is_accepted', (!topic.is_accepted).toString());
                await updateLegalResearchTopic(contextData.token, id, formData);
                updateTopics((topics) =>
                    topics.map((t) =>
                        t.id === id ? { ...t, is_accepted: !t.is_accepted } : t
                    )
                );
            } else {
                const formData = new FormData();
                formData.append('is_accepted', (!topic.is_accepted).toString());
                await updateLegalResearchTopic(contextData.token, id, formData);
                updateTopics((topics) =>
                    topics.map((t) =>
                        t.id === id ? { ...t, is_accepted: !t.is_accepted } : t
                    )
                );
            }
        } catch (error) {
            console.error("Error updating topic:", error);
            showToast(t("FailedToUpdateTopic"), "error");
        } finally {
            setSubmitting(prev => ({ ...prev, [id]: false }));
        }
    };
    
    const sortedTopics = [...formState].sort((a, b) => {
        if (a.priority == null) return 1;
        if (b.priority == null) return -1;
        return Number(a.priority) - Number(b.priority);
    });
    
    const acceptedTopics = [...formState]
        .filter((t) => t.is_accepted)
        .sort((a, b) => {
            if (a.priority == null) return 1;
            if (b.priority == null) return -1;
            return Number(a.priority) - Number(b.priority);
        });
    
    const isOAGBOrGeneralAttorney = contextData?.user?.roles?.some(
        (p) => p === "OAGBManagementHead" || p === "GeneralAttorney"
    );
    
    const topicsToRender = isOAGBOrGeneralAttorney
        ? acceptedTopics
        : sortedTopics;
    
    const unacceptedTopics = [...formState]
        .filter((t) => !t.is_accepted)
        .sort((a, b) => {
            if (a.priority == null) return 1;
            if (b.priority == null) return -1;
            return Number(a.priority) - Number(b.priority);
        });

    const teamStatus = formState.reduce((acc, topic) => {
        if (topic.team_name) {
            acc[topic.team_name] = topic.is_accepted;
        }
        return acc;
    }, {} as Record<string, boolean>);
    
    const teams = Object.keys(teamStatus);
    const submittedTeams = teams.filter(team => teamStatus[team]);

    const titlePresentationSchedule = schedules?.find(
        (s) => s.purpose === "title_presentation"
    );

    const proposalPresentationSchedule = schedules?.find(
        (s) => s.purpose === "proposal_presentation"
    );

    const getRemainingDays = (scheduledDate: string) => {
        if (!scheduledDate) return null;
        const scheduled = new Date(scheduledDate);
        const now = new Date();
        const diffTime = scheduled.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const remainingDays = titlePresentationSchedule 
        ? getRemainingDays(titlePresentationSchedule.scheduled_datetime) 
        : null;

    const proposalRemainingDays = proposalPresentationSchedule 
        ? getRemainingDays(proposalPresentationSchedule.scheduled_datetime) 
        : null;

    return (
        <div className="mx-auto mt-6">
            {/* Header */}
            <div className="border-l-4 border-[#8B0000] pl-4 mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{t("ResearchTopics")}</h1>
                <p className="text-gray-600 text-sm mt-1">{t("ResearchTopicsDescription")}</p>
            </div>

            {/* Team Submission Status Card */}
            {teams.length > 0 && contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") && (
                <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-300 rounded-lg shadow-sm p-5 mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-gray-100 rounded-lg">
                            <Users className="w-5 h-5 text-gray-700" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{t("TeamSubmissionStatus")}</h3>
                            <p className="text-sm text-gray-600">{t("TeamSubmissionStatusDescription")}</p>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {teams.map(team => (
                            <div 
                                key={team} 
                                className={`flex items-center justify-between p-3 rounded-lg border ${teamStatus[team] ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-2 h-2 rounded-full ${teamStatus[team] ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                                    <span className="font-medium text-gray-800">{team}</span>
                                </div>
                                <div className={`px-2 py-1 rounded text-xs font-medium ${teamStatus[team] ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {teamStatus[team] ? t("ForwardedToCoordinator") : t("NotForwarded")}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-green-600"></div>
                                <span>{t("ForwardedToCoordinatorCount", { count: submittedTeams.length })}</span>
                            </div>
                            <div className="flex items-center gap-1 ml-4">
                                <div className="w-2 h-2 rounded-full bg-red-600"></div>
                                <span>{t("NotForwardedCount", { count: teams.length - submittedTeams.length })}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Title Presentation Schedule Countdown Card */}
            {titlePresentationSchedule && remainingDays !== null && remainingDays !== undefined && (
                <div className={`bg-gradient-to-r rounded-lg shadow-sm p-5 mb-8 ${
                    remainingDays < 0 
                        ? 'from-red-50 to-red-100 border border-red-300' 
                        : remainingDays === 0 
                        ? 'from-orange-50 to-orange-100 border border-orange-300'
                        : remainingDays <= 3 
                        ? 'from-yellow-50 to-yellow-100 border border-yellow-300'
                        : 'from-blue-50 to-blue-100 border border-blue-300'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-full ${
                                remainingDays < 0 
                                    ? 'bg-red-100' 
                                    : remainingDays === 0 
                                    ? 'bg-orange-100'
                                    : remainingDays <= 3 
                                    ? 'bg-yellow-100'
                                    : 'bg-blue-100'
                            }`}>
                                <Clock className={`w-6 h-6 ${
                                    remainingDays < 0 
                                        ? 'text-red-600' 
                                        : remainingDays === 0 
                                        ? 'text-orange-600'
                                        : remainingDays <= 3 
                                        ? 'text-yellow-600'
                                        : 'text-blue-600'
                                }`} />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{t("TitlePresentation")}</h3>
                                <p className="text-sm text-gray-600">
                                    {titlePresentationSchedule.location ? `${t("Location")}: ${titlePresentationSchedule.location}` : t("ScheduledPresentation")}
                                    {titlePresentationSchedule.remark && ` - ${titlePresentationSchedule.remark}`}
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className={`text-3xl font-bold ${
                                remainingDays < 0 
                                    ? 'text-red-600' 
                                    : remainingDays === 0 
                                    ? 'text-orange-600'
                                    : remainingDays <= 3 
                                    ? 'text-yellow-600'
                                    : 'text-blue-600'
                            }`}>
                                {remainingDays < 0 
                                    ? `${Math.abs(remainingDays)}`
                                    : remainingDays
                                }
                            </div>
                            <div className={`text-sm font-medium ${
                                remainingDays < 0 
                                    ? 'text-red-600' 
                                    : remainingDays === 0 
                                    ? 'text-orange-600'
                                    : remainingDays <= 3 
                                    ? 'text-yellow-600'
                                    : 'text-blue-600'
                            }`}>
                                {remainingDays < 0 
                                    ? t("DaysOverdue")
                                    : remainingDays === 0 
                                    ? t("Today")
                                    : t("DaysRemaining")
                                }
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {new Date(titlePresentationSchedule.scheduled_datetime).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Proposal Presentation Schedule Countdown Card */}
            {proposalPresentationSchedule && proposalRemainingDays !== null && proposalRemainingDays !== undefined && (
                <div className={`bg-gradient-to-r rounded-lg shadow-sm p-5 mb-8 ${
                    proposalRemainingDays < 0 
                        ? 'from-red-50 to-red-100 border border-red-300' 
                        : proposalRemainingDays === 0 
                        ? 'from-orange-50 to-orange-100 border border-orange-300'
                        : proposalRemainingDays <= 3 
                        ? 'from-yellow-50 to-yellow-100 border border-yellow-300'
                        : 'from-purple-50 to-purple-100 border border-purple-300'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-full ${
                                proposalRemainingDays < 0 
                                    ? 'bg-red-100' 
                                    : proposalRemainingDays === 0 
                                    ? 'bg-orange-100'
                                    : proposalRemainingDays <= 3 
                                    ? 'bg-yellow-100'
                                    : 'bg-purple-100'
                            }`}>
                                <Clock className={`w-6 h-6 ${
                                    proposalRemainingDays < 0 
                                        ? 'text-red-600' 
                                        : proposalRemainingDays === 0 
                                        ? 'text-orange-600'
                                        : proposalRemainingDays <= 3 
                                        ? 'text-yellow-600'
                                        : 'text-purple-600'
                                }`} />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{t("ProposalPresentation")}</h3>
                                <p className="text-sm text-gray-600">
                                    {proposalPresentationSchedule.location ? `${t("Location")}: ${proposalPresentationSchedule.location}` : t("ScheduledPresentation")}
                                    {proposalPresentationSchedule.remark && ` - ${proposalPresentationSchedule.remark}`}
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className={`text-3xl font-bold ${
                                proposalRemainingDays < 0 
                                    ? 'text-red-600' 
                                    : proposalRemainingDays === 0 
                                    ? 'text-orange-600'
                                    : proposalRemainingDays <= 3 
                                    ? 'text-yellow-600'
                                    : 'text-purple-600'
                            }`}>
                                {proposalRemainingDays < 0 
                                    ? `${Math.abs(proposalRemainingDays)}`
                                    : proposalRemainingDays
                                }
                            </div>
                            <div className={`text-sm font-medium ${
                                proposalRemainingDays < 0 
                                    ? 'text-red-600' 
                                    : proposalRemainingDays === 0 
                                    ? 'text-orange-600'
                                    : proposalRemainingDays <= 3 
                                    ? 'text-yellow-600'
                                    : 'text-purple-600'
                            }`}>
                                {proposalRemainingDays < 0 
                                    ? t("DaysOverdue")
                                    : proposalRemainingDays === 0 
                                    ? t("Today")
                                    : t("DaysRemaining")
                                }
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {new Date(proposalPresentationSchedule.scheduled_datetime).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Topics List */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">{t("ResearchTopicsCount", { count: topicsToRender.length })}</h2>
                    <div className="flex gap-2 items-center">
                        {canEditTopics && !isAddingTopic && (
                            <button
                                onClick={handleStartAddTopic}
                                className="flex items-center gap-1 px-3 py-1.5 bg-[#8B0000] text-white rounded-lg hover:bg-[#7A0000] transition-colors text-sm font-medium"
                            >
                                <Plus className="w-4 h-4" />
                                <span>{t("AddTopic")}</span>
                            </button>
                        )}
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
                            <span className="text-xs text-gray-600">{t("Accepted")}</span>
                        </div>
                        <div className="flex items-center gap-1 ml-3">
                            <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300"></div>
                            <span className="text-xs text-gray-600">{t("Pending")}</span>
                        </div>
                    </div>
                </div>

                {/* Add New Topic Form */}
                {isAddingTopic && (
                    <div className="mb-4 p-4 border-2 border-dashed border-[#8B0000]/30 rounded-lg bg-gray-50">
                        <div className="flex flex-col gap-3">
                            <label className="text-sm font-medium text-gray-700">{t("NewTopicTitle")}</label>
                            <input
                                type="text"
                                value={newTopicTitle}
                                onChange={(e) => setNewTopicTitle(e.target.value)}
                                placeholder={t("EnterTopicTitle")}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B0000] focus:border-[#8B0000] outline-none"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveNewTopic();
                                    if (e.key === 'Escape') handleCancelAddTopic();
                                }}
                                autoFocus
                            />
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={handleCancelAddTopic}
                                    disabled={submitting['new_topic']}
                                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                                >
                                    <X className="w-4 h-4" />
                                    <span>{t("Cancel")}</span>
                                </button>
                                <button
                                    onClick={handleSaveNewTopic}
                                    disabled={submitting['new_topic'] || !newTopicTitle.trim()}
                                    className={`flex items-center gap-1 px-3 py-1.5 bg-[#8B0000] text-white rounded-lg hover:bg-[#7A0000] transition-colors text-sm font-medium ${
                                        submitting['new_topic'] || !newTopicTitle.trim() ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                >
                                    {submitting['new_topic'] ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>{t("Saving")}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            <span>{t("SaveTopic")}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={topicsToRender.map((t) => t.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-3">
                            {topicsToRender.map((topic, index) => (
                                <SortableItem
                                    key={topic.id}
                                    id={topic.id}
                                    topic={topic}
                                    index={index}
                                    status={status}
                                    edit={edit}
                                    toggleAccepted={toggleAccepted}
                                    toggleApproved={toggleApproved}
                                    submitting={submitting}
                                    forward_status={forward_status}
                                    canEditTopics={canEditTopics}
                                    editingTopicId={editingTopicId}
                                    editingTopicTitle={editingTopicTitle}
                                    setEditingTopicTitle={setEditingTopicTitle}
                                    handleStartEdit={handleStartEdit}
                                    handleCancelEdit={handleCancelEdit}
                                    handleSaveEdit={handleSaveEdit}
                                    handleDeleteTopic={handleDeleteTopic}
                                    deletingTopicId={deletingTopicId}
                                    remainingDays={remainingDays}
                                    titlePresentationSchedule={titlePresentationSchedule}
                                    proposalRemainingDays={proposalRemainingDays}
                                    proposalPresentationSchedule={proposalPresentationSchedule}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>

            {/* Unaccepted Topics Accordion - for all users except OAGBManagementHead */}
            {!isOAGBOrGeneralAttorney && unacceptedTopics.length > 0 && (
                <div className="mb-8">
                    <button
                        onClick={() => setIsUnacceptedOpen(!isUnacceptedOpen)}
                        className="flex items-center justify-between w-full px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700">{t("PendingTopics")}</span>
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                                {unacceptedTopics.length}
                            </span>
                        </div>
                        <svg
                            className={`w-5 h-5 text-gray-500 transition-transform ${isUnacceptedOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {isUnacceptedOpen && (
                        <div className="mt-4 space-y-3">
                            {unacceptedTopics.map((topic, index) => (
                                <SortableItem
                                    key={topic.id}
                                    id={topic.id}
                                    topic={topic}
                                    index={index}
                                    status={status}
                                    edit={edit}
                                    toggleAccepted={toggleAccepted}
                                    toggleApproved={toggleApproved}
                                    submitting={submitting}
                                    forward_status={forward_status}
                                    canEditTopics={canEditTopics}
                                    editingTopicId={editingTopicId}
                                    editingTopicTitle={editingTopicTitle}
                                    setEditingTopicTitle={setEditingTopicTitle}
                                    handleStartEdit={handleStartEdit}
                                    handleCancelEdit={handleCancelEdit}
                                    handleSaveEdit={handleSaveEdit}
                                    handleDeleteTopic={handleDeleteTopic}
                                    deletingTopicId={deletingTopicId}
                                    remainingDays={remainingDays}
                                    titlePresentationSchedule={titlePresentationSchedule}
                                    proposalRemainingDays={proposalRemainingDays}
                                    proposalPresentationSchedule={proposalPresentationSchedule}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Finding Assessment Section */}
            {finding_assesement && (
                <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-300 rounded-lg shadow-sm p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-[#8B0000]/10 rounded-lg">
                            <FileText className="w-5 h-5 text-[#8B0000]" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{t("FindingAssessment")}</h3>
                            <p className="text-sm text-gray-600">{t("FindingAssessmentDescription")}</p>
                        </div>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <FileView
                            title={t("FindingAssessment")}
                            file={finding_assesement}
                            fileName={
                                finding_assesement.substring(
                                    finding_assesement.lastIndexOf("/") + 1
                                )
                            }
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

interface SortableItemProps {
    id: string;
    topic: Topic;
    index: number;
    status: string;
    edit: boolean;
    toggleAccepted: (id: string) => void;
    toggleApproved: (id: string) => void;
    submitting: Record<string, boolean>;
    forward_status?: string;
    canEditTopics: boolean;
    editingTopicId: string | null;
    editingTopicTitle: string;
    setEditingTopicTitle: (title: string) => void;
    handleStartEdit: (topic: Topic) => void;
    handleCancelEdit: () => void;
    handleSaveEdit: (topicId: string) => Promise<void>;
    handleDeleteTopic: (topicId: string) => Promise<void>;
    deletingTopicId: string | null;
    remainingDays?: number | null;
    titlePresentationSchedule?: {
        id: string;
        scheduled_datetime: string;
        purpose: string;
        remark?: string;
        location?: string;
    } | null;
    proposalRemainingDays?: number | null;
    proposalPresentationSchedule?: {
        id: string;
        scheduled_datetime: string;
        purpose: string;
        remark?: string;
        location?: string;
    } | null;
}

function SortableItem({ 
    id, 
    topic, 
    index, 
    toggleAccepted, 
    toggleApproved, 
    status, 
    edit, 
    submitting, 
    forward_status,
    canEditTopics,
    editingTopicId,
    editingTopicTitle,
    setEditingTopicTitle,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteTopic,
    deletingTopicId,
    remainingDays,
    titlePresentationSchedule,
    proposalRemainingDays,
    proposalPresentationSchedule
}: SortableItemProps) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });

    const contextData = useAuth();
    const navigate = useNavigate();
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`group relative border rounded-lg shadow-sm transition-all duration-200 ${
                topic.is_accepted 
                    ? 'border-green-200 bg-green-50/50 hover:bg-green-50' 
                    : 'border-gray-200 bg-white hover:bg-gray-50'
            } ${topic.is_approved ? 'ring-1 ring-[#8B0000]/20' : ''}`}
        >
            {/* Priority Badge */}
            {topic.priority && (
                <div className="absolute -left-2 -top-2">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-900 text-white text-sm font-bold rounded-full shadow-lg">
                        #{topic.priority}
                    </div>
                </div>
            )}

            <div className="p-5">
                <div className="flex items-start justify-between">
                    {/* Left Section */}
                    <div className="flex-1 pr-4">
                        <div className="flex items-start gap-4">
                            {/* Drag Handle */}
                            {(contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
                              contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") ||
                              contextData?.user?.roles?.some((p) => p === "GeneralAttorney")) &&
                            (status === LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value ||
                             status === LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value ||
                             edit) && (
                                <button
                                    {...attributes}
                                    {...listeners}
                                    className="mt-1 p-1 rounded hover:bg-gray-100 transition-colors"
                                >
                                    <GripVertical className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                                </button>
                            )}
                            
                            {/* Topic Content */}
                            <div className="flex-1">
                                {editingTopicId === topic.id ? (
                                    <div className="flex flex-col gap-2">
                                        <input
                                            type="text"
                                            value={editingTopicTitle}
                                            onChange={(e) => setEditingTopicTitle(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B0000] focus:border-[#8B0000] outline-none text-lg font-semibold"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSaveEdit(topic.id);
                                                if (e.key === 'Escape') handleCancelEdit();
                                            }}
                                            autoFocus
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleSaveEdit(topic.id)}
                                                disabled={submitting[topic.id] || !editingTopicTitle.trim()}
                                                className={"flex items-center gap-1 px-3 py-1.5 bg-[#8B0000] text-white rounded-lg hover:bg-[#7A0000] transition-colors text-sm " + (submitting[topic.id] || !editingTopicTitle.trim() ? 'opacity-50 cursor-not-allowed' : '')}
                                            >
                                                <Save className="w-4 h-4" />
                                                <span>{t("Save")}</span>
                                            </button>
                                            <button
                                                onClick={handleCancelEdit}
                                                disabled={submitting[topic.id]}
                                                className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                                            >
                                                <X className="w-4 h-4" />
                                                <span>{t("Cancel")}</span>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-start justify-between">
                                        <h3 className="font-semibold text-gray-900 mb-2 text-lg leading-tight flex-1">
                                            {topic.topic_title}
                                        </h3>
                                        {canEditTopics && !topic.is_accepted && (
                                            <div className="flex gap-1 ml-2">
                                                <button
                                                    onClick={() => handleStartEdit(topic)}
                                                    className="p-1.5 text-gray-500 hover:text-[#8B0000] hover:bg-gray-100 rounded transition-colors"
                                                    title={t("EditTopic")}
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteTopic(topic.id)}
                                                    disabled={deletingTopicId === topic.id}
                                                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                    title={t("RemoveTopic")}
                                                >
                                                    {deletingTopicId === topic.id ? (
                                                        <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                                                    ) : (
                                                        <Trash2 className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {editingTopicId !== topic.id && (
                                <div className="flex flex-wrap gap-3 mt-3">
                                    {topic.team_name && (
                                        <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm text-gray-700">
                                            <Users className="w-3 h-3" />
                                            <span>{topic.team_name}</span>
                                        </div>
                                    )}
                                    
                                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm text-gray-700">
                                        <span className="text-xs text-gray-500">{t("Created")}:</span>
                                        <span>{new Date(topic.created_at).toLocaleDateString()}</span>
                                    </div>
                                    
                                    {topic.is_accepted && (
                                        <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                                            <Check className="w-3 h-3" />
                                            <span>{t("Accepted")}</span>
                                        </div>
                                    )}
                                    
                                    {topic.is_approved && (
                                        <div className="flex items-center gap-1 px-2 py-1 bg-[#8B0000]/10 text-[#8B0000] rounded text-sm">
                                            <Check className="w-3 h-3" />
                                            <span>{t("Approved")}</span>
                                        </div>
                                    )}
                                    
                                    {/* Title Presentation Schedule Countdown */}
                                    {remainingDays !== null && remainingDays !== undefined && titlePresentationSchedule && (
                                        <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                                            remainingDays < 0 
                                                ? 'bg-red-100 text-red-800' 
                                                : remainingDays === 0 
                                                ? 'bg-orange-100 text-orange-800'
                                                : remainingDays <= 3 
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-blue-100 text-blue-800'
                                        }`}>
                                            <Clock className="w-3 h-3" />
                                            <span>
                                                {remainingDays < 0 
                                                    ? t("OverdueByDays", { count: Math.abs(remainingDays) })
                                                    : remainingDays === 0 
                                                    ? t("Today")
                                                    : t("DaysLeft", { count: remainingDays })
                                                }
                                            </span>
                                        </div>
                                    )}

                                    {/* Proposal Presentation Schedule Countdown */}
                                    {proposalRemainingDays !== null && proposalRemainingDays !== undefined && proposalPresentationSchedule && (
                                        <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                                            proposalRemainingDays < 0 
                                                ? 'bg-red-100 text-red-800' 
                                                : proposalRemainingDays === 0 
                                                ? 'bg-orange-100 text-orange-800'
                                                : proposalRemainingDays <= 3 
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-purple-100 text-purple-800'
                                        }`}>
                                            <Clock className="w-3 h-3" />
                                            <span>
                                                {proposalRemainingDays < 0 
                                                    ? t("OverdueByDays", { count: Math.abs(proposalRemainingDays) })
                                                    : proposalRemainingDays === 0 
                                                    ? t("Today")
                                                    : t("DaysLeft", { count: proposalRemainingDays })
                                                }
                                            </span>
                                        </div>
                                    )}
                                </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Section - Actions */}
                    <div className="flex items-center gap-4">
                        {/* Acceptance Toggle - For Coordinator when forwarded_to_coordinator */}
                        {(contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") &&
                          (status === LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value ||
                           status === LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value) &&
                          forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value) && (
                            <div className="flex flex-col items-center gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={topic.is_accepted}
                                        onChange={() => toggleAccepted(topic.id)}
                                        disabled={submitting[id]}
                                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">{t("Accepted")}</span>
                                </label>
                            </div>
                        )}

                        {/* Acceptance Toggle - For GeneralAttorney when forwarded_to_head */}
                        {(contextData?.user?.roles?.some((p) => p === "GeneralAttorney") &&
                          (status === LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value ||
                           status === LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value) &&
                          forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value) && (
                            <div className="flex flex-col items-center gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={topic.is_accepted}
                                        onChange={() => toggleAccepted(topic.id)}
                                        disabled={submitting[id]}
                                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">{t("Accepted")}</span>
                                </label>
                            </div>
                        )}

                        {/* Acceptance Toggle - For Director when forwarded_to_director, forwarded_to_coordinator, or forwarded directly to the prosecutors */}
                        {(contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
                          (status === LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value ||
                           status === LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value) &&
                          (forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value ||
                           forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value ||
                           forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value)) && (
                            <div className="flex flex-col items-center gap-2">
                                <button
                                    onClick={() => toggleAccepted(topic.id)}
                                    disabled={submitting[id] || status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        topic.is_accepted
                                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                            : status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value
                                            ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                    } ${submitting[id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {submitting[id] ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                            <span>{t("Processing")}</span>
                                        </>
                                    ) : status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value ? (
                                        <>
                                            <Check className="w-4 h-4" />
                                            <span>{t("Forward")}</span>
                                        </>
                                    ) : topic.is_accepted ? (
                                        <>
                                            <Check className="w-4 h-4" />
                                            <span>{t("Accepted")}</span>
                                        </>
                                    ) : (
                                        <>
                                            <AlertCircle className="w-4 h-4" />
                                            <span>{t("Accept")}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Approval Toggle - Only for OAGBManagementHead */}
                        {(status !== LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value &&
                          status !== LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value) && (
                            <div className="flex items-center gap-4">
                                {contextData?.user?.roles?.some((p) => p === "OAGBManagementHead") && 
                                 (status === LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value || 
                                  status === LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value || 
                                  edit) && (
                                    <button
                                        onClick={() => toggleApproved(topic.id)}
                                        disabled={submitting[id]}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            topic.is_approved 
                                                ? 'bg-[#8B0000] text-white hover:bg-[#7A0000]' 
                                                : 'bg-gray-800 text-white hover:bg-gray-900'
                                        } ${submitting[id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting[id] ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>{t("Processing")}</span>
                                            </>
                                        ) : topic.is_approved ? (
                                            <>
                                                <Check className="w-4 h-4" />
                                                <span>{t("Approved")}</span>
                                            </>
                                        ) : (
                                            <>
                                                <AlertCircle className="w-4 h-4" />
                                                <span>{t("Approve")}</span>
                                            </>
                                        )}
                                    </button>
                                )}

                                {/* View Research Button */}
                                {topic.is_approved && 
                                 ((status !== LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value && 
                                   status !== LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value) ||
                                  (status === LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value && 
                                   contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator"))) &&
                                  topic.internal_research_request && (
                                    <button
                                        onClick={() => navigate(`/conduct-legal-research/internal/${topic.internal_research_request}`)}
                                        className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                                    >
                                        <Eye className="w-4 h-4" />
                                        <span>{t("ViewResearch")}</span>
                                    </button>
                                )}

                                {/* Proposal Presentation Remaining Days Badge */}
                                {proposalRemainingDays !== null && proposalRemainingDays !== undefined && proposalPresentationSchedule && (
                                    <div className="relative group">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium cursor-help ${proposalRemainingDays < 0 ? 'bg-red-100 text-red-800' : proposalRemainingDays === 0 ? 'bg-orange-100 text-orange-800' : proposalRemainingDays <= 3 ? 'bg-yellow-100 text-yellow-800' : 'bg-purple-100 text-purple-800'}`}>
                                            <Clock className="w-3 h-3" />
                                            {proposalRemainingDays < 0 ? `${Math.abs(proposalRemainingDays)}d ${t("Overdue")}` : proposalRemainingDays === 0 ? t("Today") : `${proposalRemainingDays}d ${t("Left")}`}
                                        </span>
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                                            <p className="font-semibold">{t("ProposalPresentation")}</p>
                                            <p>{proposalPresentationSchedule.location || t("NoLocationSpecified")}</p>
                                            <p>{proposalPresentationSchedule.remark || t("NoRemarks")}</p>
                                            <p className="text-gray-400 mt-1">
                                                {new Date(proposalPresentationSchedule.scheduled_datetime).toLocaleDateString('en-US', {weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Status Indicator Bar */}
            <div className={`h-1 rounded-b-lg ${
                topic.is_approved ? 'bg-[#8B0000]' :
                topic.is_accepted ? 'bg-green-600' :
                'bg-red-900'
            }`}></div>
        </div>
    );
}